// `/app/balance` (`spec/17-frontend.md` §8.1, `spec/tasks/WEB-53.md`). Sem step-up: são GETs.
//
// O estado da tela vive na query string (`spec/17` §3): aba, busca, período e o lançamento
// aberto no drawer. O detalhe tem URL (`spec/24` §1 decisão 5), aqui como `?entry=<id>` e
// não como rota `$id`, porque `files_allowed` desta task não inclui um arquivo de rota
// novo. Ver "Questões abertas" da task.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { useMode } from '../app/mode.tsx';
import { BalanceScreen, type BalanceSearch } from '../features/balance/balance-screen.tsx';
import type { TabId } from '../features/balance/ledger.ts';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const TAB_VALUES = ['all', 'in', 'out', 'reserve', 'withdraw'] as const satisfies readonly TabId[];

/** Query string tolerante a lixo: `?tab=banana` cai na aba Tudo em vez de derrubar a rota. */
const searchSchema = z.object({
  // `tab` opcional: a aba default não suja a URL, e lixo (`?tab=banana`) cai nela.
  tab: z.enum(TAB_VALUES).optional().catch(undefined),
  q: z.string().min(1).optional().catch(undefined),
  entry: z.string().min(1).optional().catch(undefined),
  from: z.string().regex(ISO_DATE).optional().catch(undefined),
  to: z.string().regex(ISO_DATE).optional().catch(undefined),
});

function Balance() {
  const raw = Route.useSearch();
  const navigate = useNavigate();
  const { mode } = useMode();
  const search: BalanceSearch = { ...raw, tab: raw.tab ?? 'all' };

  const apply = (next: BalanceSearch): void => {
    void navigate({
      to: '/app/balance',
      search: {
        ...(next.tab === 'all' ? {} : { tab: next.tab }),
        ...(next.q === undefined ? {} : { q: next.q }),
        ...(next.entry === undefined ? {} : { entry: next.entry }),
        ...(next.from === undefined ? {} : { from: next.from }),
        ...(next.to === undefined ? {} : { to: next.to }),
      },
      // Abrir e fechar o drawer é navegação de verdade (o botão de voltar tem de fechá-lo);
      // trocar filtro, não, senão o histórico vira uma tecla por letra digitada.
      replace: next.entry === search.entry,
    });
  };

  return (
    <BalanceScreen
      mode={mode}
      search={search}
      onSearch={apply}
      onWithdraw={() => {
        void navigate({ to: '/app/withdrawals' });
      }}
      onOpenTransaction={(id) => {
        void navigate({ to: '/app/transactions/$id', params: { id } });
      }}
      onOpenWithdrawal={(id) => {
        void navigate({ to: '/app/withdrawals/$id', params: { id } });
      }}
    />
  );
}

export const Route = createFileRoute('/app/balance/')({
  validateSearch: searchSchema,
  component: Balance,
});
